import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import useStyles from '@front10/helpers/dist/UseStyles';
import { isSSR } from '@front10/utils';
import withCommonEMContainer from '../_commons/CommonEMContainer';

//-------------------------------------
// Basic components here
//-------------------------------------
import Box from '../../elements/Box';
import Row from '../../elements/Row';
import Col from '../../elements/Col';
import Header from '../../elements/Header';
import TravelsGuide from './TravelsGuide';
import NoFaresBehavior from '../../others/NoFaresBehavior/NoFaresBehavior';
import SkeletonTravelsGuideContainer from '../../others/Skeleton/SkeletonTravelsGuideContainer';
import SkeletonTravelsGuideContainerMobile from '../../others/Skeleton/SkeletonTravelsGuideContainerMobile';
import ContainerErrorAlert from '../../alerts/ContainerErrorAlert';
import NoResults from '../../others/NoResults';
import { Context } from '@front10/helpers/dist/Context';

//-------------------------------------
// EveryMundo integration
//-------------------------------------
import { getTravelsGuide } from '../../../_integrations/everymundo/travelsGuide';
import { useTravels } from './hooks/travels';
function TravelsGuideContainer(props) {
  const {
    prePopulation,
    integrationSettings,
    skeletonSettings,
    showSkeletonScreen,
    direction,
    showNoResultsPlaceholder,
    colContainerSettings,
    notResults,
    alert,
    showTitle,
    mainContentSettings,
    title,
    theme,
    initialData,
    isMobile = false,
    travelGuideSettings,
    meta,
    pageContext,
    enableSEO = true,
    getTravelsGuide: getTravelsGuideFn
  } = props;

  const [className, style] = useStyles(props, { direction, theme });
  const myStyle = Object.assign({ minHeight: '450px' }, style);
  const { translate } = useContext(Context);

  const { travelsGuide, match, retry, dispatch } = useTravels({
    prePopulation,
    integrationSettings,
    initialData,
    getTravelsGuide: getTravelsGuideFn,
    showNoResultsPlaceholder,
    meta,
    pageContext
  });

  if (isSSR() && !Boolean(enableSEO)) {
    return null;
  }

  if (match('HIDDEN')) {
    return <NoFaresBehavior />;
  }

  return (
    <Box
      position="relative"
      className={`${className} ${TravelsGuideContainer.componentName}`}
      dataTestId="travelsGuideContainer"
      style={myStyle}
    >
      {showSkeletonScreen &&
        match('SKELETON') &&
        (!isMobile ? (
          <SkeletonTravelsGuideContainer {...skeletonSettings} position="absolute" width="100" />
        ) : (
          <SkeletonTravelsGuideContainerMobile
            {...skeletonSettings}
            position="absolute"
            width="100"
          />
        ))}
      {showTitle && (
        <Row gutters>
          <Col>
            <Header
              tag="h2"
              dataTestId="headerTitle"
              fontSize="5"
              content={
                title.content || translate('cmp-travel-guide-container-title', 'Travel Guide')
              }
              {...title}
            />
          </Col>
        </Row>
      )}
      <Row gutters {...mainContentSettings} style={{ position: 'relative' }}>
        {match('ERROR') && (
          <Col>
            <ContainerErrorAlert {...alert} tryAgain={retry} />
          </Col>
        )}
        {match('NO_RESULTS') && (
          <NoResults
            labelKey="cmp-no-results-text"
            buttonLabelKey="cmp-no-results-button"
            buttonRefreshingLabelKey="cmp-no-results-button-refreshing"
            icon="broken-heart"
            showIcon
            showButton
            onClick={retry}
            {...notResults}
          />
        )}

        {match('COMPLETE') && (
          <Col {...colContainerSettings}>
            <TravelsGuide
              isMobile={isMobile}
              data={travelsGuide}
              dispatch={dispatch}
              {...travelGuideSettings}
            />
          </Col>
        )}
      </Row>
    </Box>
  );
}

TravelsGuideContainer.propTypes = {
  /**
   * Props of alerts
   */
  alert: PropTypes.objectOf(PropTypes.any),
  /**
   * Direction of the component rtl o ltr
   */
  direction: PropTypes.oneOf(['rtl', 'ltr']),
  /**
   * Function for request to obtain data
   */
  getTravelsGuide: PropTypes.func,
  /**
   * All integration configurations
   */
  integrationSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * mainContentSettings props
   */
  mainContentSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * NotResults Component
   */
  notResults: PropTypes.objectOf(PropTypes.any),
  /**
   * Component PrePopulation
   */
  prePopulation: PropTypes.objectOf(PropTypes.any),
  /**
   * Determine if the component will show the skeleton screen while
   * loading (Default value will be false)
   */
  showSkeletonScreen: PropTypes.bool,
  /**
   * Define if show noResultsPlaceHolder
   */
  showNoResultsPlaceholder: PropTypes.bool,
  /**
   * Determine if the component will show the title
   */
  showTitle: PropTypes.bool,
  /**
   * SkeletonSettings Component
   */
  skeletonSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * Title Component
   */
  title: PropTypes.objectOf(PropTypes.any),
  /** LocalizationData via moment to be used as a date translator
   * https://github.com/moment/moment/blob/develop/src/locale/es.js
   */
  localizationData: PropTypes.objectOf(PropTypes.any),
  /**
   * Theme object on the component
   */
  theme: PropTypes.objectOf(PropTypes.any),
  /**
   * Type of skeleton to render
   */
  initialData: PropTypes.arrayOf(PropTypes.any)
};

TravelsGuideContainer.defaultProps = {
  alert: {
    bgColor: 'secondary-4',
    textAlign: 'center',
    padding: '3',
    rounded: '@theme:card.rounded'
  },
  direction: 'ltr',
  getTravelsGuide: getTravelsGuide,
  integrationSettings: {},
  mainContentSettings: {
    marginTop: '3'
  },
  prePopulation: {
    queryFields: [
      {
        destination: 'outloc'
      },
      {
        languageCode: 'lang'
      },
      {
        departureDate: 'outdate'
      },
      {
        maxItemsAmount: 'top'
      }
    ],
    destination: 'ATH', // Destination Airport Code
    departureDate: null, // Today's date (dd/mm/YYYY)
    languageCode: 'EN', // Requested language (en,
    maxItemsAmount: 3
  },
  showSkeletonScreen: true,
  showNoResultsPlaceholder: true,
  notResults: {},
  showTitle: true,
  skeletonSettings: {
    style: { backgroundColor: 'white' }
  },
  title: {
    content: ''
  },
  localizationData: null,
  theme: null,
  initialData: []
};
TravelsGuideContainer.componentName = 'TravelsGuideContainer';

// HOC with common functionality to all containers
export default withCommonEMContainer(TravelsGuideContainer);
